import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Copy, CheckCircle2, Mail, UserPlus, KeyRound } from "lucide-react";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";

const CreateClientModal = ({ open, onClose, onCreated }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", nature_of_engagement: "", period: "", employee_name: "", employee_email: "", client_contact_email: "" });
  const [busy, setBusy] = useState(false);
  const [invites, setInvites] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const reset = () => { setForm({ name: "", nature_of_engagement: "", period: "", employee_name: "", employee_email: "", client_contact_email: "" }); setInvites(null); };
  const close = () => { reset(); onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/clients", form);
      setInvites(data.invites);
      onCreated && onCreated();
      toast({ title: "Client created", description: `${form.name} added. Invites generated below.` });
    } catch (err) {
      toast({ title: "Could not create client", description: err?.response?.data?.detail || "Try again.", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const copy = (inv) => {
    navigator.clipboard.writeText(`Email: ${inv.email}\nPassword: ${inv.temp_password}\nRole: ${inv.role}`);
    toast({ title: "Copied invite credentials" });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
        {!invites ? (
          <>
            <DialogHeader>
              <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50"><UserPlus className="h-6 w-6 text-indigo-700" /></div>
              <DialogTitle className="text-2xl font-bold text-[#1a1a2e]">Add a client</DialogTitle>
              <DialogDescription className="text-slate-500">The client contact and the handling employee will each receive an invite to register.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="mt-2 space-y-3">
              <div className="space-y-1.5"><Label>Client name</Label><Input required value={form.name} onChange={set("name")} placeholder="Acme Pvt Ltd" className="rounded-xl" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Nature of engagement</Label><Input required value={form.nature_of_engagement} onChange={set("nature_of_engagement")} placeholder="Statutory Audit" className="rounded-xl" /></div>
                <div className="space-y-1.5"><Label>Period</Label><Input required value={form.period} onChange={set("period")} placeholder="FY 2025-26" className="rounded-xl" /></div>
              </div>
              <div className="rounded-xl bg-slate-50 p-4 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Request Center staff (firm side)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Name</Label><Input required value={form.employee_name} onChange={set("employee_name")} placeholder="Priya Nair" className="rounded-xl bg-white" /></div>
                  <div className="space-y-1.5"><Label>Email</Label><Input required type="email" value={form.employee_email} onChange={set("employee_email")} placeholder="priya@firm.co" className="rounded-xl bg-white" /></div>
                </div>
              </div>
              <div className="rounded-xl bg-slate-50 p-4 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Client contact (Client Workspace)</p>
                <div className="space-y-1.5"><Label>Email</Label><Input required type="email" value={form.client_contact_email} onChange={set("client_contact_email")} placeholder="cfo@acme.co" className="rounded-xl bg-white" /></div>
              </div>
              <Button type="submit" disabled={busy} className="w-full rounded-xl bg-[#2f2b8f] py-6 text-sm font-semibold text-white hover:bg-[#26236f]">
                {busy ? "Creating..." : "Create client & send invites"}
              </Button>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50"><CheckCircle2 className="h-6 w-6 text-emerald-600" /></div>
              <DialogTitle className="text-2xl font-bold text-[#1a1a2e]">Client created</DialogTitle>
              <DialogDescription className="text-slate-500">Invitation emails have been sent. Each person signs in with their email and a one-time code — no password to share.</DialogDescription>
            </DialogHeader>
            <div className="mt-2 space-y-3">
              {invites.length === 0 && <p className="text-sm text-slate-500">Both users already existed — no new invites generated.</p>}
              {invites.map((inv) => (
                <div key={inv.email} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                  <div className="flex items-center justify-between">
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${inv.role === "employee" ? "bg-emerald-100 text-emerald-700" : "bg-violet-100 text-violet-700"}`}>{inv.role}</span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700"><KeyRound className="h-3 w-3" /> Passwordless</span>
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-sm text-slate-700"><Mail className="h-4 w-4 text-slate-400" /> {inv.email}</div>
                  <div className="mt-1 text-xs text-slate-500">Invited to sign in with a one-time email code.</div>
                </div>
              ))}
              <Button onClick={close} className="w-full rounded-xl bg-[#2f2b8f] py-6 text-sm font-semibold text-white hover:bg-[#26236f]">Done</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreateClientModal;
